import ButtonT from '@components/tailwind-components/Button/ButtonT';
import CheckboxTogglerT from '@components/tailwind-components/Input/CheckboxTogglerT';
import FileUploadT from '@components/tailwind-components/Upload/FileUploadT';
import type { TFile } from '@conversed/shared/src/central/types/common.type';
import { t } from 'i18next';
import type React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { CheckboxInputT } from '../tailwind-components/Input/CheckboxInputT';
import { NumberInputT } from '../tailwind-components/Input/NumberInputT';
import { SelectInputT } from '../tailwind-components/Input/SelectInputT';
import { Modal } from '../tailwind-components/Modal/Modal';

/* eslint-disable i18next/no-literal-string */

const DemoModalBodyComponent = () => (
  <p className='p-6 bg-surface-50'>Modal body is passed as a component</p>
);

export const TailwindDemoPage: React.FC = () => {
  const [openBasicModal, setOpenBasicModal] = useState(false);
  const { control } = useForm({});

  const handleFileUpload = (file: TFile | string) => {
    console.log('Received file:', file);
  };

  const section = (
    <>
      <section className='pt-10 h-dvh overflow-y-auto pr-0'>
        <div className='container mx-auto'>
          <h1 className='!bg-primary-base text-white text-xl p-2 rounded-md text-center mb-10'>
            Conversed Theme - Frontend{' '}
            <span className='text-xs'>(TAILWIND CSS)</span>
          </h1>
          <div className='text-left mb-2 border-b text-xl font-bold'>
            Colors-scheme
          </div>
          <div className='flex'>
            <div className='flex items-center'>
              <div className='w-5 h-5 rounded-md !bg-primary-base' />
              <span className='text-xs ml-2'>Primary</span>
            </div>

            <div className='flex items-center ml-2'>
              <div className='w-5 h-5 rounded-md bg-secondary' />
              <span className='text-xs ml-2'>Secondary</span>
            </div>

            <div className='flex items-center ml-2'>
              <div className='w-5 h-5 rounded-md bg-primary-base' />
              <span className='text-xs ml-2'>Info</span>
            </div>

            <div className='flex items-center ml-2'>
              <div className='w-5 h-5 rounded-md bg-warning' />
              <span className='text-xs ml-2'>Warning</span>
            </div>

            <div className='flex items-center ml-2'>
              <div className='w-5 h-5 rounded-md bg-danger' />
              <span className='text-xs ml-2'>Danger</span>
            </div>

            <div className='flex items-center ml-2'>
              <div className='w-5 h-5 rounded-md bg-success' />
              <span className='text-xs ml-2'>Success</span>
            </div>

            <div className='flex items-center ml-2'>
              <div className='w-5 h-5 rounded-md bg-gray-600' />
              <span className='text-xs ml-2'>Conversed</span>
            </div>
          </div>
          <span className='text-pico font-light block mb-3 mt-2'>
            This variables can be customized in{' '}
            <strong>tailwind.config.js</strong>
          </span>
          <div className='text-left mb-2 border-b text-xl font-bold'>
            Font-Sizes
          </div>
          <div className='mb-3'>
            <span className='text-timestamp ml-2'>Timestamp</span>
            <span className='text-pico ml-2'>Pico</span>
            <span className='text-tiny ml-2'>Tiny</span>
            <span className='text-xs ml-2'>XS</span>
            <span className='text-sm ml-2'>SM</span>
            <span className='text-base ml-2'>Base</span>
            <span className='text-lg ml-2'>LG</span>
            <span className='text-lgHeading ml-2'>LG Heading</span>
            <span className='text-xl ml-2'>XL</span>
            <span className='text-2xl ml-2'>2-XL</span>
            <span className='text-3xl ml-2'>3-XL</span>
          </div>
          <div className='text-left mb-2 border-b text-xl font-bold'>
            Font-Weight
          </div>
          <div className='mb-3'>
            <span className='text-sm font-thin ml-2'>Thin</span>
            <span className='text-sm font-light ml-2'>Light</span>
            <span className='text-sm font-semibold ml-2'>Semibold</span>
            <span className='text-sm font-bold ml-2'>Bold</span>
            <span className='text-sm italic ml-2'>Italic</span>
            <span className='text-sm underline ml-2'>Underline</span>
          </div>

          <div className='text-left mb-2 border-b text-xl font-bold'>
            Modals
          </div>
          <div
            className='text-white !bg-primary-base rounded-md py-2 px-3 my-3 inline-block cursor-pointer'
            onClick={() => setOpenBasicModal(!openBasicModal)}>
            <div>Open tailwind Modal</div>
          </div>

          <div className='my-10'>
            <div className='text-left my-3 border-b text-xl font-bold'>
              Button <strong>(with variations)</strong>
            </div>
            <div className='text-left my-3 border-b text-sm font-thin'>
              Size: <strong>(xs - sm - md - lg)</strong> | Type:{' '}
              <strong>(submit - button)</strong> | Styling:{' '}
              <strong>(primary - secondary - danger - info - warning)</strong> |
              Rounding: <strong>(xs - sm - md - full)</strong>
            </div>
            <ButtonT
              styling='primary'
              type='button'
              className='mr-2'
              rounding='full'
              onClick={() => console.log('Button clicked')}>
              Click me
            </ButtonT>

            <ButtonT
              styling='success'
              type='button'
              rounding='full'
              onClick={() => console.log('Button clicked')}>
              Click me
            </ButtonT>

            <ButtonT
              styling='secondary'
              type='button'
              rounding='sm'
              className='mx-3'
              onClick={() => console.log('Another button clicked')}>
              Another Button
            </ButtonT>

            <ButtonT
              styling='danger'
              type='button'
              rounding='sm'
              size='large'
              className='mx-3'
              onClick={() => console.log('Another button clicked')}>
              Another Button
            </ButtonT>

            <ButtonT
              styling='info'
              type='button'
              rounding='full'
              size='md'
              className='mx-3'
              onClick={() => console.log('Another button clicked')}>
              Another Button
            </ButtonT>

            <ButtonT
              styling='warning'
              type='button'
              rounding='md'
              className='mx-3'
              onClick={() => console.log('Another button clicked')}>
              Another Button
            </ButtonT>
          </div>

          <Modal
            isOpen={openBasicModal}
            onClose={() => setOpenBasicModal(false)}
            header='test modal'
            body={<DemoModalBodyComponent />}
            bodyClasses='bg-white-50'
            footer='Basic footer'
            footerClasses='text-sm font-semibold'
          />

          <div className='text-left mb-3 border-b text-xl font-bold'>
            Checkbox with Toggle <strong>(CheckboxTogglerT default)</strong>
          </div>
          <CheckboxTogglerT
            toggleSize='large'
            control={control}
            field={'tailwindDemo.checkbox'}
          />
          <br />
          <div className='text-left mb-3 border-b text-xl font-bold'>
            Checkbox with Toggle <strong>(CheckboxTogglerT alternative)</strong>
          </div>

          <CheckboxTogglerT
            toggleSize='large'
            colorScheme='alternative'
            control={control}
            field={'tailwindDemo.checkbox2'}
          />

          <div className='text-left mt-3 mb-3 border-b text-xl font-bold'>
            Checkbox <strong>(CheckboxT)</strong>
          </div>

          <CheckboxInputT
            id='text.v2.allow.validator'
            field={'tailwindDemo.checkbox'}
            label={t('flow.form.description.allow-validator')}
            control={control}
          />

          <div className='text-left my-3 border-b text-xl font-bold'>
            Input <strong>(Number)</strong>
          </div>
          <NumberInputT />

          <div className='text-left my-3 border-b text-xl font-bold'>
            Select <strong>(with options)</strong>
          </div>
          <SelectInputT
            values={[
              {
                value: 'tesst',
                description: 'desc1',
              },
              {
                value: 'tesst1',
                description: 'desc2',
              },
            ]}
          />

          <div className='text-left my-3 border-b text-xl font-bold'>
            Upload image <strong>(with fileURL)</strong>
          </div>
          <p className='text-sm'>
            {' '}
            Includes possibility to get image from URL and add file type also
            instead of image{' '}
          </p>

          <FileUploadT accept={['image/*']} onFileUpload={handleFileUpload} />
        </div>
      </section>
    </>
  );
  return section;
};
